import React, { useState, useEffect, useCallback, useRef } from 'react';
import {useTranslation} from 'react-i18next';import {X, BookOpen, ChevronsLeft, ChevronsRight, Columns, Square, Highlighter, Trash2, Download, CheckCircle, Loader2, MessageSquare, Check, Share2, ListChecks} from 'lucide-react';import {Button} from '@/components/ui/button';import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';import {Input} from '@/components/ui/input';import {ScrollArea} from '@/components/ui/scroll-area';import {BIBLE_BOOKS, getLocalizedBookName, resolveToEnglishBookName} from '@/utils/bibleData';import {getCachedPassage, cachePassage, bulkCachePassages, getDownloadedTranslations, deleteTranslationCache, TOTAL_BIBLE_CHAPTERS} from '@/utils/bibleOfflineDB';import {useDrag} from '@use-gesture/react';import {useSpring, animated} from '@react-spring/web';import axios from 'axios';
import ShareVerseModal from './ShareVerseModal';
import {toast} from 'sonner';import {API_URL as API} from '@/utils/apiConfig';
import { useSignInGate } from '@/hooks/useSignInGate';


const HIGHLIGHT_COLORS = [
  { id: 'yellow', bg: 'rgba(250, 204, 21, 0.3)', border: 'rgba(250, 204, 21, 0.6)' },
  { id: 'green', bg: 'rgba(74, 222, 128, 0.3)', border: 'rgba(74, 222, 128, 0.6)' },
  { id: 'blue', bg: 'rgba(96, 165, 250, 0.3)', border: 'rgba(96, 165, 250, 0.6)' },
  { id: 'pink', bg: 'rgba(244, 114, 182, 0.3)', border: 'rgba(244, 114, 182, 0.6)' },
  { id: 'purple', bg: 'rgba(167, 139, 250, 0.3)', border: 'rgba(167, 139, 250, 0.6)' },
];

function getHighlightStyle(colorId) {
  const c = HIGHLIGHT_COLORS.find(h => h.id === colorId) || HIGHLIGHT_COLORS[0];
  return { backgroundColor: c.bg, borderBottom: `2px solid ${c.border}` };
}

// Single verse component with highlight, selection, and long-press-to-share support
function VerseItem({ verse, reference, translation, isHighlighted, highlightColor, onToggleHighlight, isTarget, isSelected, selectMode, onToggleSelect, onLongPressShare }) {
  const longPressTimerRef = React.useRef(null);
  const longPressFiredRef = React.useRef(false);

  const handleClick = (e) => {
    if (longPressFiredRef.current) {
      e.preventDefault();
      longPressFiredRef.current = false;
      return;
    }
    if (selectMode) {
      onToggleSelect(verse);
    } else {
      onToggleHighlight(verse, reference, translation);
    }
  };

  // Long-press (≈550ms) opens the share sheet for this single verse.
  // We track on pointer events so it works equally on mouse + touch +
  // pencil. Cancelled on pointer-up / move / leave.
  const startLongPress = () => {
    longPressFiredRef.current = false;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPressShare?.(verse, reference, translation);
    }, 550);
  };
  const cancelLongPress = () => {
    clearTimeout(longPressTimerRef.current);
  };

  return (
    <>
      {verse.heading && (
        <h3
          className="block mt-4 mb-1 text-[0.85em] font-semibold uppercase tracking-wide text-primary/70"
          data-testid={`verse-heading-${verse.verse}`}
        >
          {verse.heading}
        </h3>
      )}
      <span
        className={`inline cursor-pointer rounded-sm px-0.5 transition-all duration-500 hover:bg-muted/40 ${isTarget ? 'animate-verse-flash' : ''} ${isSelected ? 'ring-2 ring-primary/50 bg-primary/10 rounded' : ''}`}
        style={!isSelected && isHighlighted ? getHighlightStyle(highlightColor) : {}}
        onClick={handleClick}
        onPointerDown={onLongPressShare && !selectMode ? startLongPress : undefined}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onContextMenu={onLongPressShare && !selectMode ? (e) => { e.preventDefault(); } : undefined}
        data-testid={`verse-${verse.verse}`}
      >
        {selectMode && (
          <span className={`inline-flex items-center justify-center w-4 h-4 rounded-sm mr-0.5 align-middle text-[10px] ${isSelected ? 'bg-primary text-primary-foreground' : 'border border-muted-foreground/30'}`}>
            {isSelected && <Check className="h-3 w-3" />}
          </span>
        )}
        <sup className="text-[0.6em] font-semibold text-primary/40 mr-1 select-none">{verse.verse}</sup>
        {verse.text}{' '}
      </span>
    </>
  );
}

// Swipeable highlight item for the highlights tab
function SwipeableHighlightItem({ highlight, index, onDelete, onNavigate }) {
  const [{ x }, api] = useSpring(() => ({ x: 0 }));

  const bind = useDrag(({ active, movement: [mx], cancel }) => {
    if (mx > 0) {
      if (active) api.start({ x: 0, immediate: true });
      return;
    }
    const TRIGGER = -80;
    if (!active && mx < TRIGGER) {
      api.start({ x: -300, config: { duration: 200 } });
      setTimeout(() => onDelete(highlight), 220);
    } else {
      api.start({ x: active ? mx : 0, immediate: active });
    }
  }, { axis: 'x', filterTaps: true, from: () => [x.get(), 0] });

  const hlColor = HIGHLIGHT_COLORS.find(c => c.id === highlight.color) || HIGHLIGHT_COLORS[0];

  return (
    <div className="relative rounded-xl overflow-hidden touch-none" data-testid={`highlight-item-${index}`}>
      {/* Red delete background revealed on swipe */}
      <div className="absolute inset-0 bg-destructive/15 flex items-center justify-end px-5 rounded-xl">
        <Trash2 className="h-5 w-5 text-destructive" />
      </div>

      <animated.div
        {...bind()}
        style={{ x }}
        className="relative rounded-xl border border-border p-3 cursor-pointer hover:bg-muted/30 transition-colors bg-card"
        onClick={() => onNavigate(highlight)}
      >
        <div style={{ borderLeftWidth: '4px', borderLeftColor: hlColor.border, borderLeftStyle: 'solid', paddingLeft: '10px' }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-primary uppercase tracking-wide">
              {highlight.reference}:{highlight.verse_num}
            </span>
            <span className="text-[10px] text-muted-foreground uppercase">{highlight.translation}</span>
          </div>
          <p className="text-sm text-foreground leading-relaxed line-clamp-3">{highlight.text}</p>
        </div>
      </animated.div>
    </div>
  );
}

const COPYRIGHT_NOTICES = {
  esv: 'Scripture quotations are from The ESV\u00ae Bible (The Holy Bible, English Standard Version\u00ae), \u00a9 2001 by Crossway, a publishing ministry of Good News Publishers. Used by permission. All rights reserved.',
  amp: 'Scripture quotations taken from the Amplified\u00ae Bible (AMP), Copyright \u00a9 2015 by The Lockman Foundation. Used by permission. lockman.org',
  nasb: 'Scripture quotations taken from the (NASB\u00ae) New American Standard Bible\u00ae, Copyright \u00a9 1960, 1971, 1977, 1995 by The Lockman Foundation. Used by permission. lockman.org',
  lbla: 'Scripture quotations taken from La Biblia de las Am\u00e9ricas\u00ae (LBLA), Copyright \u00a9 1986, 1995, 1997 by The Lockman Foundation. Used by permission. lockman.org',
  // LSB permissions (lsbible.org/permission-to-quote-the-lsb) require the
  // full copyright notice AND a click-enabled link to LSBible.org whenever
  // the text is rendered on a web page or in an app.
  lsb: (
    <>
      Scripture quotations taken from the (LSB<sup>&reg;</sup>) Legacy Standard Bible<sup>&reg;</sup>, Copyright &copy; 2021 by The Lockman Foundation. Used by permission. All rights reserved. Managed in partnership with Three Sixteen Publishing Inc.{' '}
      <a
        href="https://lsbible.org/"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
        data-testid="lsb-copyright-link"
      >
        LSBible.org
      </a>
      {' '}and{' '}
      <a
        href="https://316publishing.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        316publishing.com
      </a>
      .
    </>
  ),
};

function CopyrightFooter({ translations }) {
  const notices = translations
    .map(t => COPYRIGHT_NOTICES[t])
    .filter(Boolean);
  if (notices.length === 0) return null;
  return (
    <div className="px-4 py-2 border-t border-border bg-muted/20" data-testid="bible-copyright-footer">
      {notices.map((n, i) => (
        <p key={i} className="text-[9px] text-muted-foreground/60 leading-relaxed">{n}</p>
      ))}
    </div>
  );
}

export default function BibleReader({ onClose, userTranslations = ['asv'], targetReference = null, onDiscuss, standalone = false, user = null }) {
  const { t, i18n } = useTranslation();
  const { requireSignIn, signInModal } = useSignInGate(user);
  const lang = i18n.language.split('-')[0];
  const localizeBook = (name) => getLocalizedBookName(name, lang);

  const getDefaultTranslations = () => {
    const lang = i18n.language.split('-')[0];
    switch(lang) {
      case 'es': return ['rvr09', 'esv'];
      case 'fr': return ['ls1910', 'esv'];
      case 'pt': return ['almeida', 'esv'];
      default: return ['asv', 'esv'];
    }
  };

  // Restore translations from localStorage or use defaults
  const [translation1, setTranslation1] = useState(() =>
    localStorage.getItem('bible_translation1') || getDefaultTranslations()[0]
  );
  const [translation2, setTranslation2] = useState(() =>
    localStorage.getItem('bible_translation2') || getDefaultTranslations()[1]
  );

  // Auto-switch translation when language changes
  const prevLangRef = useRef(i18n.language.split('-')[0]);
  useEffect(() => {
    const lang = i18n.language.split('-')[0];
    if (lang === prevLangRef.current) return;
    prevLangRef.current = lang;
    const defaults = getDefaultTranslations();
    setTranslation1(defaults[0]);
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation state from localStorage
  const [selectedBook, setSelectedBook] = useState(() => localStorage.getItem('bible_book') || 'Genesis');
  const [selectedChapter, setSelectedChapter] = useState(() => localStorage.getItem('bible_chapter') || '1');
  const [selectedVerse, setSelectedVerse] = useState('');
  const [reference, setReference] = useState(() => {
    const b = localStorage.getItem('bible_book') || 'Genesis';
    const c = localStorage.getItem('bible_chapter') || '1';
    return `${b} ${c}`;
  });

  const [verses1, setVerses1] = useState([]);
  const [verses2, setVerses2] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [isParallel, setIsParallel] = useState(false);
  const manualLookupRef = React.useRef(!!targetReference); // Skip initial localStorage fetch when opening via scripture link
  const fetchIdRef = React.useRef(0); // Track latest fetch to prevent stale responses
  const fetch2IdRef = React.useRef(0); // Stale-protection for pane 2
  const [searchResults, setSearchResults] = useState(null); // Verse search results

  // ── Share Verse modal state ─────────────────────────────────────────
  // `shareTarget` holds { reference, verses, translation } when the
  // user opens the modal (via long-press or the multi-select toolbar).
  const [shareTarget, setShareTarget] = useState(null);

  // ── Pane 2 (parallel mode) navigation — independent of pane 1 ──────
  // Persisted separately so the user's "compare against X" stays put
  // across sessions. Initialized from pane 1 the first time the user
  // enables parallel in a fresh install.
  const [selectedBook2, setSelectedBook2] = useState(() => localStorage.getItem('bible_book2') || localStorage.getItem('bible_book') || 'Genesis');
  const [selectedChapter2, setSelectedChapter2] = useState(() => localStorage.getItem('bible_chapter2') || localStorage.getItem('bible_chapter') || '1');

  // ── Verse-range scoping ─────────────────────────────────────────────
  // When a passage like "Psalm 1:1-4" is opened from a study plan / chat
  // / daily reflection, we still fetch the FULL chapter (cheaper, lets
  // the user expand) but render only the requested range. The
  // "Read full chapter" button at the bottom expands to the rest.
  // Shape: { start: 1, end: 4 } — null means "show whole chapter".
  const [verseRange, setVerseRange] = useState(null);
  const [rangeExpanded, setRangeExpanded] = useState(false);

  // Highlights
  const [highlights, setHighlights] = useState([]);
  const [activeHighlightColor, setActiveHighlightColor] = useState('yellow');
  const [activeTab, setActiveTab] = useState('reader'); // 'reader' | 'highlights' | 'offline'
  const [targetVerseNum, setTargetVerseNum] = useState(null);

  // Verse selection for "Discuss with Eili"
  const [selectMode, setSelectMode] = useState(false);
  const [selectedVerses, setSelectedVerses] = useState(new Set());

  const availableTranslations = [
    { id: 'kjv', name: t('generated.biblereader_king_james_version_cb02') }, { id: 'esv', name: t('generated.biblereader_english_standard_version_a325') }, { id: 'amp', name: t('generated.biblereader_amplified_bible_amp_2238') },
    { id: 'nasb', name: t('generated.biblereader_new_american_standard_5866') }, { id: 'lsb', name: t('generated.biblereader_legacy_standard_bible_3e31') },
    { id: 'asv', name: t('generated.biblereader_american_standard_version_e4a3') }, { id: 'web', name: t('generated.biblereader_world_english_bible_7483') },
    { id: 'ylt', name: t('generated.biblereader_young_s_literal_9a03') }, { id: 'rvr09', name: t('generated.biblereader_reina_valera_1909_c3ec') }, { id: 'ls1910', name: t('generated.biblereader_louis_segond_1910_5521') },
    { id: 'almeida', name: t('generated.biblereader_jo_o_ferreira_2203') }, { id: 'lbla', name: t('generated.biblereader_la_biblia_de_9083') }
  ];

  const getChapterCount = (bookName) => {
    const book = BIBLE_BOOKS.find(b => b.name === bookName);
    return book ? book.chapters.length : 0;
  };

  // Persist translations on change
  useEffect(() => {
    localStorage.setItem('bible_translation1', translation1);
  }, [translation1]);
  useEffect(() => {
    localStorage.setItem('bible_translation2', translation2);
  }, [translation2]);

  // Load highlights
  useEffect(() => {
    const loadHighlights = async () => {
      try {
        const res = await axios.get(`${API}/bible/highlights`, { withCredentials: true });
        setHighlights(res.data || []);
      } catch {
        // Fallback to localStorage for guests
        const stored = localStorage.getItem('bible_highlights');
        if (stored) setHighlights(JSON.parse(stored));
      }
    };
    loadHighlights();
  }, []);

  // Parse reference string and sync selectors (without triggering fetch)
  const parseAndSyncReference = (refStr) => {
    const parts = refStr.match(/((?:(?:1|2|3)\s?)?[A-Za-z]+(?: (?:of )?[A-Za-z]+)*)\s+(\d+)(?::([\d\-\.,]+))?/);
    if (parts) {
      const rawBookName = parts[1].trim();
      const resolved = resolveToEnglishBookName(rawBookName);
      const chapter = parts[2];
      const verseStr = parts[3] || '1';
      const startVerse = verseStr.split('-')[0].split(',')[0];
      
      const matchedBook = BIBLE_BOOKS.find(b => b.name.toLowerCase() === resolved.toLowerCase());
      if (matchedBook) {
        manualLookupRef.current = true; // Prevent useEffect from overwriting reference
        setSelectedBook(matchedBook.name);
        setSelectedChapter(chapter);
        setSelectedVerse(startVerse);
        localStorage.setItem('bible_book', matchedBook.name);
        localStorage.setItem('bible_chapter', chapter);
        localStorage.setItem('bible_verse', startVerse);
      }
    }
  };

  // Detect if input looks like a Bible reference (e.g. "John 3:16") vs natural language
  const isBibleReference = (text) => {
    return /^(?:(?:1|2|3)\s?)?[A-Za-z]+(?: (?:of )?[A-Za-z]+)*\s+\d+(?::\d+(?:\s*[-–]\s*\d+)?)?$/.test(text.trim());
  };

  // Manual lookup from input field — handles both exact references and natural language search
  const handleManualLookup = async () => {
    const query = reference.trim();
    if (!query) return;
    
    if (isBibleReference(query)) {
      // Exact reference — use existing passage fetch
      setSearchResults(null);
      parseAndSyncReference(query);
      fetchPassage(query);
    } else {
      // Natural language search — find matching verses
      setSearchResults(null);
      setLoading(true);
      try {
        const res = await axios.post(`${API}/bible/search`, { 
          query, 
          translation: translation1 
        }, { withCredentials: true });
        if (res.data?.results?.length > 0) {
          setSearchResults(res.data.results);
        } else {
          toast.error(t('bible.no_results', 'No matching verses found'));
        }
      } catch (err) {
        const msg = err.response?.data?.detail || t('bible.search_failed', 'Search failed. Try a different query.');
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  // Navigate to a search result — loads the full chapter and scrolls to the verse
  const handleSearchResultClick = (result) => {
    setSearchResults(null);
    setReference(result.reference);
    parseAndSyncReference(result.reference);
    fetchPassage(result.reference);
  };

  // Fetch passage for pane 1 — always fetch fresh from API with stale-request protection.
  // Pane 2 has its own independent fetcher (`fetchPane2`) since parallel
  // mode now allows comparing entirely different books/chapters.
  const fetchPassage = useCallback(async (refOverride) => {
    const refToUse = typeof refOverride === 'string' ? refOverride : reference;
    if (!refToUse) return;
    const thisRequestId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const bust = `_v=${Date.now()}`;
      const result = await axios
        .post(`${API}/bible/passage?${bust}`, { reference: refToUse, translation: translation1 })
        .catch((err) => ({ __error: err }));

      // Discard stale response if a newer fetch was initiated
      if (thisRequestId !== fetchIdRef.current) return;

      if (result && !result.__error) {
        const data = result.data;
        setVerses1(data.verses || [{ verse: 1, text: data.text }]);
        cachePassage(refToUse, translation1, data);
      } else {
        // Fallback to offline cache only if network fails
        const cached1 = await getCachedPassage(refToUse, translation1);
        if (thisRequestId !== fetchIdRef.current) return;
        if (cached1) {
          setVerses1(cached1.verses || [{ verse: 1, text: cached1.text }]);
        } else {
          setVerses1([{ verse: 1, text: t('bible.failed_load') }]);
        }
      }
    } catch {
      if (thisRequestId === fetchIdRef.current) toast.error(t('bible.failed_lookup'));
    } finally {
      if (thisRequestId === fetchIdRef.current) setLoading(false);
    }
  }, [reference, translation1, t]);

  // Fetch passage for pane 2 — independent stale-protection + cache fallback.
  const fetchPane2 = useCallback(async (refOverride) => {
    const refToUse = typeof refOverride === 'string' ? refOverride : `${selectedBook2} ${selectedChapter2}`;
    if (!refToUse) return;
    const thisRequestId = ++fetch2IdRef.current;
    setLoading2(true);
    try {
      const bust = `_v=${Date.now()}`;
      const result = await axios
        .post(`${API}/bible/passage?${bust}`, { reference: refToUse, translation: translation2 })
        .catch((err) => ({ __error: err }));

      if (thisRequestId !== fetch2IdRef.current) return;

      if (result && !result.__error) {
        const data = result.data;
        setVerses2(data.verses || [{ verse: 1, text: data.text }]);
        cachePassage(refToUse, translation2, data);
      } else {
        const cached2 = await getCachedPassage(refToUse, translation2);
        if (thisRequestId !== fetch2IdRef.current) return;
        if (cached2) {
          setVerses2(cached2.verses || [{ verse: 1, text: cached2.text }]);
        } else {
          setVerses2([{ verse: 1, text: t('bible.failed_load') }]);
        }
      }
    } finally {
      if (thisRequestId === fetch2IdRef.current) setLoading2(false);
    }
  }, [selectedBook2, selectedChapter2, translation2, t]);

  // Scroll to a specific verse and flash it
  const scrollToVerse = useCallback((verseNum) => {
    if (!verseNum) return;
    setTargetVerseNum(parseInt(verseNum));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="verse-${verseNum}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    // Clear flash after animation completes
    setTimeout(() => setTargetVerseNum(null), 2500);
  }, []);

  // Sync selectors -> reference -> fetch (always full chapter)
  useEffect(() => {
    if (selectedBook && selectedChapter) {
      // Skip if this was triggered by a manual lookup (which already has the correct reference with range)
      if (manualLookupRef.current) {
        manualLookupRef.current = false;
        return;
      }
      const ref = `${selectedBook} ${selectedChapter}`;
      setReference(ref);
      localStorage.setItem('bible_book', selectedBook);
      localStorage.setItem('bible_chapter', selectedChapter);
      fetchPassage(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook, selectedChapter]);

  // When verse changes, just scroll to it (don't re-fetch)
  useEffect(() => {
    if (selectedVerse && verses1.length > 0) {
      scrollToVerse(selectedVerse);
    }
  }, [selectedVerse, verses1, scrollToVerse]);

  // Re-fetch pane 1 when its translation changes
  useEffect(() => {
    if (verses1.length > 0) fetchPassage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translation1]);

  // Persist pane 2 selection + fetch pane 2 whenever its inputs change.
  // Only fires when parallel mode is active; pane 2 verses are cleared
  // when leaving parallel so the next entry can re-mirror pane 1.
  useEffect(() => {
    localStorage.setItem('bible_book2', selectedBook2);
    localStorage.setItem('bible_chapter2', selectedChapter2);
    if (isParallel) {
      fetchPane2();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParallel, selectedBook2, selectedChapter2, translation2]);

  // Toggle parallel mode: when turning ON, mirror pane 1 so the user
  // sees the same passage in both translations until they explicitly
  // navigate pane 2 elsewhere. When turning OFF, clear pane 2 verses
  // so the layout collapses cleanly.
  const toggleParallel = () => {
    if (!isParallel) {
      setSelectedBook2(selectedBook);
      setSelectedChapter2(selectedChapter);
    } else {
      setVerses2([]);
    }
    setIsParallel(!isParallel);
  };

  // React to targetReference (from chat scripture clicks / plans / daily reflections)
  // Accepts either:
  //   • An object `{ book, chapter, verse }` (StudyPlanDayPage path)
  //   • A string like "2 Kings 20:20", "1 Cor 13:1-4", "Psalm 23" (Did You
  //     Know, Today Reading Card, etc.)
  useEffect(() => {
    if (!targetReference) return;

    // Normalize string references into the same {book, chapter, verse}
    // shape this effect was originally built for. Robust parser:
    //   • Optional leading book number (1/2/3 or roman)
    //   • Book name (one or more words, letters + dots, allows "Song of Songs")
    //   • Chapter (digits)
    //   • Optional :verse (with optional -range or ,list)
    let parsed = targetReference;
    if (typeof targetReference === 'string') {
      const s = String(targetReference).trim();
      // Regex: capture book (digits/words allowed), chapter, optional verse spec.
      const m = s.match(/^\s*((?:[1-3]\s+)?[A-Za-z][A-Za-z. ]*?)\s+(\d+)(?::(\d+(?:[-,]\s*\d+)*))?\s*$/);
      if (m) {
        parsed = {
          book: m[1].trim().replace(/\.+$/, ''),
          chapter: parseInt(m[2], 10),
          verse: m[3] ? m[3].replace(/\s+/g, '') : '',
        };
      } else {
        // Fallback: split on the LAST whitespace before a digit — handles
        // unusual spacing. If still no match, just open the book.
        const altMatch = s.match(/^(.+?)\s+(\d+)$/);
        if (altMatch) {
          parsed = {
            book: altMatch[1].trim(),
            chapter: parseInt(altMatch[2], 10),
            verse: '',
          };
        } else {
          parsed = { book: s, chapter: 1, verse: '' };
        }
      }
    }

    const { book: rawBook, chapter, verse } = parsed || {};
    if (!rawBook || !chapter) return;
    const verseStr = verse ? verse.toString() : '';

    // Normalize book name to match BIBLE_BOOKS canonical names
    const resolved = resolveToEnglishBookName(rawBook);
    const matchedBook = BIBLE_BOOKS.find(b => b.name.toLowerCase() === resolved.toLowerCase());
    const book = matchedBook ? matchedBook.name : resolved;

    manualLookupRef.current = true;
    if (book) setSelectedBook(book);
    if (chapter) setSelectedChapter(chapter.toString());
    setSelectedVerse(verseStr.split('-')[0].split(',')[0]);

    // Parse a possible verse range (e.g. "1-4" or "12-15") so we can
    // restrict the rendered output to just those verses. Single-verse
    // ("16") and no-verse ("") cases set range to null = full chapter.
    let parsedRange = null;
    if (verseStr.includes('-')) {
      const [a, b] = verseStr.split('-').map(s => parseInt(s.trim(), 10));
      if (!Number.isNaN(a) && !Number.isNaN(b) && b > a) {
        parsedRange = { start: a, end: b };
      }
    }
    setVerseRange(parsedRange);
    setRangeExpanded(false);

    // Always fetch the full chapter (cheaper than per-verse calls and
    // lets us expand inline without a second network round-trip)
    const fullRef = `${book} ${chapter}`;
    setReference(fullRef);
    fetchPassage(fullRef).then(() => {
      if (verseStr) scrollToVerse(verseStr.split('-')[0].split(',')[0]);
    });
    setActiveTab('reader');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetReference]);

  // Toggle highlight on a verse
  const handleToggleHighlight = async (verse, ref, trans) => {
    // Guests cannot persist highlights — nudge them to sign in.
    if (requireSignIn('highlight verses')) return;
    const refBase = `${selectedBook} ${selectedChapter}`;
    const existingIdx = highlights.findIndex(
      h => h.reference === refBase && h.verse_num === verse.verse
    );

    if (existingIdx >= 0) {
      // Remove highlight
      const updated = highlights.filter((_, i) => i !== existingIdx);
      setHighlights(updated);
      localStorage.setItem('bible_highlights', JSON.stringify(updated));
      try {
        await axios.delete(`${API}/bible/highlights?reference=${encodeURIComponent(refBase)}&verse_num=${verse.verse}`, { withCredentials: true });
      } catch { /* guest fallback: localStorage already updated */ }
    } else {
      // Add highlight
      const newHighlight = {
        reference: refBase,
        verse_num: verse.verse,
        text: verse.text,
        translation: trans,
        color: activeHighlightColor,
        created_at: new Date().toISOString()
      };
      const updated = [newHighlight, ...highlights];
      setHighlights(updated);
      localStorage.setItem('bible_highlights', JSON.stringify(updated));
      try {
        await axios.post(`${API}/bible/highlights`, newHighlight, { withCredentials: true });
      } catch { /* guest fallback: localStorage already updated */ }
    }
  };

  const removeHighlight = async (highlight) => {
    const updated = highlights.filter(h => !(h.reference === highlight.reference && h.verse_num === highlight.verse_num));
    setHighlights(updated);
    localStorage.setItem('bible_highlights', JSON.stringify(updated));
    try {
      await axios.delete(`${API}/bible/highlights?reference=${encodeURIComponent(highlight.reference)}&verse_num=${highlight.verse_num}`, { withCredentials: true });
    } catch { /* fallback */ }
  };

  const navigateToHighlight = (highlight) => {
    const parts = highlight.reference.match(/(.*)\s+(\d+)/);
    if (parts) {
      manualLookupRef.current = true;
      setSelectedBook(parts[1]);
      setSelectedChapter(parts[2]);
      setSelectedVerse(highlight.verse_num.toString());
      const ref = `${parts[1]} ${parts[2]}`;
      setReference(ref);
      fetchPassage(ref).then(() => scrollToVerse(highlight.verse_num.toString()));
      setActiveTab('reader');
    }
  };

  // Navigation
  const getBookIndex = (bookName) => BIBLE_BOOKS.findIndex(b => b.name === bookName);

  const handlePrevChapter = () => {
    const bIndex = getBookIndex(selectedBook);
    const cNum = parseInt(selectedChapter);
    setSelectedVerses(new Set());
    setVerseRange(null); setRangeExpanded(false);
    if (cNum > 1) { setSelectedChapter((cNum - 1).toString()); setSelectedVerse(''); }
    else if (bIndex > 0) {
      const prev = BIBLE_BOOKS[bIndex - 1];
      setSelectedBook(prev.name); setSelectedChapter(prev.chapters.length.toString()); setSelectedVerse('');
    }
  };

  const handleNextChapter = () => {
    const bIndex = getBookIndex(selectedBook);
    const cNum = parseInt(selectedChapter);
    const book = BIBLE_BOOKS[bIndex];
    setSelectedVerses(new Set());
    setVerseRange(null); setRangeExpanded(false);
    if (cNum < book.chapters.length) { setSelectedChapter((cNum + 1).toString()); setSelectedVerse(''); }
    else if (bIndex < BIBLE_BOOKS.length - 1) {
      const next = BIBLE_BOOKS[bIndex + 1];
      setSelectedBook(next.name); setSelectedChapter('1'); setSelectedVerse('');
    }
  };

  const handleToggleSelect = (verse) => {
    setSelectedVerses(prev => {
      const next = new Set(prev);
      if (next.has(verse.verse)) next.delete(verse.verse);
      else next.add(verse.verse);
      return next;
    });
  };

  const handleDiscussSelected = () => {
    const sortedNums = Array.from(selectedVerses).sort((a, b) => a - b);
    const verseTexts = sortedNums.map(num => {
      const v = verses1.find(v => v.verse === num);
      return v ? `${num}. ${v.text}` : null;
    }).filter(Boolean);
    const ref = `${selectedBook} ${selectedChapter}`;
    const scriptureBlock = `${ref}:${sortedNums.join(',')} (${translation1.toUpperCase()})\n\n${verseTexts.join('\n')}`;
    if (onDiscuss) {
      onDiscuss(scriptureBlock, ref);
    }
    setSelectedVerses(new Set());
    setSelectMode(false);
  };

  // Long-press on a single verse → open share sheet for just that verse.
  const handleLongPressShare = (verse, ref, trans) => {
    setShareTarget({
      reference: `${ref}:${verse.verse}`,
      verses: [{ verse: verse.verse, text: verse.text }],
      translation: trans,
      _seedVerse: verse.verse, // remembered so "Add more" can preselect it
    });
  };

  // Multi-select toolbar "Share" → open share sheet for the contiguous
  // range. We collapse non-contiguous selections to a range from min..max
  // and pull the actual text from the loaded pane 1 verses.
  const handleShareSelected = () => {
    if (selectedVerses.size === 0) return;
    const sortedNums = Array.from(selectedVerses).sort((a, b) => a - b);
    const start = sortedNums[0];
    const end = sortedNums[sortedNums.length - 1];
    const versesPayload = sortedNums
      .map((num) => {
        const v = verses1.find((vv) => vv.verse === num);
        return v ? { verse: num, text: v.text } : null;
      })
      .filter(Boolean);
    const ref = `${selectedBook} ${selectedChapter}:${start === end ? start : `${start}-${end}`}`;
    setShareTarget({
      reference: ref,
      verses: versesPayload,
      translation: translation1,
    });
  };

  const bIndex = getBookIndex(selectedBook);
  const cNum = parseInt(selectedChapter);
  const bookData = BIBLE_BOOKS[bIndex];
  const canPrevChapter = !(bIndex === 0 && cNum === 1);
  const canNextChapter = !(bIndex === BIBLE_BOOKS.length - 1 && cNum === bookData?.chapters.length);

  // Swipe between chapters
  const chapterContentRef = useRef(null);
  const transitionDirRef = useRef(null);

  const bindChapterSwipe = useDrag(({ active, movement: [mx], direction: [dx], velocity: [vx] }) => {
    const el = chapterContentRef.current;
    if (!el) return;
    if (active) {
      el.style.transition = 'none';
      el.style.transform = `translate3d(${mx}px, 0, 0)`;
      el.style.opacity = Math.max(0.3, 1 - Math.abs(mx) / 350);
    } else {
      const threshold = 50;
      if ((mx < -threshold || (vx > 0.3 && dx < 0)) && canNextChapter) {
        el.style.transition = 'transform 0.18s ease-out, opacity 0.18s ease-out';
        el.style.transform = 'translate3d(-105%, 0, 0)';
        el.style.opacity = '0';
        transitionDirRef.current = 'next';
        setTimeout(handleNextChapter, 180);
      } else if ((mx > threshold || (vx > 0.3 && dx > 0)) && canPrevChapter) {
        el.style.transition = 'transform 0.18s ease-out, opacity 0.18s ease-out';
        el.style.transform = 'translate3d(105%, 0, 0)';
        el.style.opacity = '0';
        transitionDirRef.current = 'prev';
        setTimeout(handlePrevChapter, 180);
      } else {
        el.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease';
        el.style.transform = 'translate3d(0, 0, 0)';
        el.style.opacity = '1';
      }
    }
  }, { axis: 'x', filterTaps: true });

  useEffect(() => {
    const el = chapterContentRef.current;
    if (!transitionDirRef.current || !el) return;
    const dir = transitionDirRef.current;
    transitionDirRef.current = null;
    el.style.transition = 'none';
    el.style.transform = `translate3d(${dir === 'next' ? '60%' : '-60%'}, 0, 0)`;
    el.style.opacity = '0';
    requestAnimationFrame(() => {
      el.style.transition = 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.28s ease';
      el.style.transform = 'translate3d(0, 0, 0)';
      el.style.opacity = '1';
    });
  }, [selectedBook, selectedChapter]);

  // Accepts an optional `paneCtx` override so pane 2 can render its
  // own book/chapter without inheriting pane 1's highlight context.
  // verseRange (study-plan partial passages) only applies to pane 1.
  const renderVerses = (verses, trans, paneCtx) => {
    const ctxBook = paneCtx?.book || selectedBook;
    const ctxChapter = paneCtx?.chapter || selectedChapter;
    const isPane2 = !!paneCtx;
    const isLoading = isPane2 ? loading2 : loading;

    if (isLoading) {
      return (
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-5/6" />
        </div>
      );
    }
    if (!verses.length) return <p className="text-muted-foreground">{t('bible.select_passage')}</p>;

    // When a verse range was passed via targetReference (e.g. study
    // plan "Psalm 1:1-4"), filter to just those verses unless the user
    // tapped "Read full chapter". Pane 2 always renders the full chapter.
    const visible = (!isPane2 && verseRange && !rangeExpanded)
      ? verses.filter(v => v.verse >= verseRange.start && v.verse <= verseRange.end)
      : verses;

    const isVerseHighlightedInCtx = (verseNum) => {
      const refBase = `${ctxBook} ${ctxChapter}`;
      return highlights.find(h => h.reference === refBase && h.verse_num === verseNum);
    };

    return (
      <>
        <p className="text-foreground leading-[2.2] text-base tracking-[0.01em]" style={{ fontFamily: 'var(--theme-font-family)' }}>
          {visible.map(v => {
            const hl = isVerseHighlightedInCtx(v.verse);
            return (
              <VerseItem
                key={v.verse}
                verse={v}
                reference={`${ctxBook} ${ctxChapter}`}
                translation={trans}
                isHighlighted={!!hl}
                highlightColor={hl?.color || 'yellow'}
                onToggleHighlight={handleToggleHighlight}
                isTarget={!isPane2 && targetVerseNum === v.verse}
                isSelected={!isPane2 && selectedVerses.has(v.verse)}
                selectMode={!isPane2 && selectMode}
                onToggleSelect={handleToggleSelect}
                onLongPressShare={handleLongPressShare}
              />
            );
          })}
        </p>

        {/* Range scope footer — only visible while we're showing a
            partial range or when the user has expanded it. Lets them
            zoom out to the full chapter (and back). Pane 1 only. */}
        {!isPane2 && verseRange && verses.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {rangeExpanded
                ? `Showing all of ${selectedBook} ${selectedChapter}.`
                : `Showing verses ${verseRange.start}–${verseRange.end} of ${selectedBook} ${selectedChapter}.`}
            </p>
            <button
              type="button"
              onClick={() => setRangeExpanded(prev => !prev)}
              className="text-xs font-medium px-3 py-1.5 rounded-md transition-colors hover:bg-muted/50"
              style={{ color: 'var(--theme-accent, hsl(var(--primary)))', border: '1px solid var(--glass-border)' }}
              data-testid="bible-toggle-range-btn"
            >
              {rangeExpanded ? `Show only ${verseRange.start}–${verseRange.end}` : 'Read full chapter'}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`flex flex-col h-full bg-card text-card-foreground shadow-2xl transition-all duration-300 ${standalone ? 'w-full' : 'border-l border-border w-full lg:w-[600px]'}`}>
      {/* Header with tabs */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/20" style={{ position: 'relative', zIndex: 2, touchAction: 'manipulation' }}>
        <div className="flex items-center gap-3">
          <BookOpen className="h-5 w-5 text-primary" />
          <div className="flex gap-1 bg-background/40 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('reader')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${activeTab === 'reader' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="bible-reader-tab"
            >
              {t('bible.reader')}
            </button>
            <button
              onClick={() => setActiveTab('highlights')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${activeTab === 'highlights' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="bible-highlights-tab"
            >
              <Highlighter className="h-3.5 w-3.5" />
              {highlights.length > 0 && <span className="text-xs">({highlights.length})</span>}
            </button>
            <button
              onClick={() => setActiveTab('offline')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${activeTab === 'offline' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="bible-offline-tab"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              // Direct share trigger — pre-loads the currently visible
              // passage so users don't have to discover "select mode"
              // first. They can still narrow down or add more verses
              // from inside the ShareVerseModal (it supports both).
              if (verses1 && verses1.length > 0) {
                setShareTarget({
                  reference: `${selectedBook} ${selectedChapter}`,
                  verses: verses1.map((v) => ({ verse: v.verse, text: v.text })),
                  translation: translation1 || 'esv',
                });
              } else {
                setSelectMode(true);
              }
            }}
            className="h-8 w-8 hover:bg-muted"
            title={t('generated.biblereader_share_this_passage_df02')}
            data-testid="bible-share-passage-btn"
          >
            <Share2 className="h-4 w-4" />
          </Button>
          <Button
            variant={selectMode ? 'default' : 'ghost'}
            size="icon"
            onClick={() => { setSelectMode(!selectMode); if (selectMode) setSelectedVerses(new Set()); }}
            className={`h-8 w-8 ${selectMode ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            title={selectMode ? 'Exit selection' : 'Select specific verses'}
            data-testid="bible-select-mode-btn"
          >
            <ListChecks className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-destructive/10 hover:text-destructive" data-testid="close-bible-reader">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
      {activeTab === 'reader' ? (
        <>
          {/* Controls */}
          <div className="p-3 border-b border-border space-y-3 bg-card" style={{ position: 'relative', zIndex: 2, touchAction: 'manipulation' }}>
            <div className="grid grid-cols-2 gap-2">
              <Select value={selectedBook} onValueChange={(val) => { setVerseRange(null); setRangeExpanded(false); setSelectedBook(val); setSelectedChapter('1'); setSelectedVerse(''); }}>
                <SelectTrigger className="bg-background border-input text-sm"><SelectValue placeholder={t('bible.book')} /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{BIBLE_BOOKS.map(b => <SelectItem key={b.name} value={b.name}>{localizeBook(b.name)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={selectedChapter} onValueChange={(val) => { setVerseRange(null); setRangeExpanded(false); setSelectedChapter(val); setSelectedVerse(''); }}>
                <SelectTrigger className="bg-background border-input text-sm"><SelectValue placeholder={t('bible.chapter')} /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{Array.from({ length: getChapterCount(selectedBook) }, (_, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Input
                placeholder={t('bible.search_placeholder', 'Search verses or enter reference...')}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="flex-1 bg-background text-foreground border-input text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleManualLookup()}
                data-testid="bible-lookup-input"
              />
              <Button onClick={handleManualLookup} disabled={loading} className="bg-primary text-primary-foreground hover:opacity-90 text-sm" data-testid="bible-lookup-btn">
                {loading ? t('bible.loading_text') : t('bible.lookup_btn')}
              </Button>
              <Button variant="outline" size="icon" onClick={toggleParallel} title={isParallel ? t('bible.switch_single') : t('bible.switch_parallel')} className="bg-background border-input hover:bg-muted" data-testid="bible-parallel-toggle">
                {isParallel ? <Square className="h-4 w-4" /> : <Columns className="h-4 w-4" />}
              </Button>
            </div>

            {/* Recall-mode hint — teaches users that search is a verse-
                FINDER, not a topical/situational guidance tool. Kept
                small and muted so it doesn't compete with the controls. */}
            <p className="text-[11px] text-muted-foreground/70 -mt-1 px-1 leading-snug" data-testid="bible-search-hint">
              {t(
                'bible.search_hint',
                'Looking for a verse? Type what you remember — a phrase, a paraphrase, or a description of the moment.'
              )}
            </p>

            <div className="flex items-center gap-2">
              <div className={`flex-1 grid ${isParallel ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                <Select value={translation1} onValueChange={setTranslation1}>
                  <SelectTrigger className="bg-background/60 border-input h-8 text-xs font-medium uppercase tracking-wide" data-testid="translation1-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{availableTranslations.map(tr => <SelectItem key={tr.id} value={tr.id}>{t(`bible_trans.${tr.id}`, tr.name)}</SelectItem>)}</SelectContent>
                </Select>
                {isParallel && (
                  <Select value={translation2} onValueChange={setTranslation2}>
                    <SelectTrigger className="bg-background/60 border-input h-8 text-xs font-medium uppercase tracking-wide" data-testid="translation2-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">{availableTranslations.map(tr => <SelectItem key={tr.id} value={tr.id}>{t(`bible_trans.${tr.id}`, tr.name)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              {/* Highlight color picker */}
              <div className="flex gap-1 items-center ml-1">
                {HIGHLIGHT_COLORS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setActiveHighlightColor(c.id)}
                    className={`w-5 h-5 rounded-full border-2 transition-transform ${activeHighlightColor === c.id ? 'scale-125 border-foreground' : 'border-transparent hover:scale-110'}`}
                    style={{ backgroundColor: c.border }}
                    title={c.id}
                    data-testid={`highlight-color-${c.id}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          {searchResults ? (
            /* Search Results Panel */
            (<ScrollArea className="flex-1 bg-card">
              <div className="px-6 py-5 lg:px-8 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {searchResults.length} {searchResults.length === 1 ? 'Result' : 'Results'} {t('generated.biblereader_found_3957')}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchResults(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid="search-results-close"
                  >
                    {t('bible.back_to_reader', 'Back to Reader')}
                  </Button>
                </div>
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSearchResultClick(result)}
                    className="w-full text-left rounded-xl p-4 space-y-2 transition-all hover:opacity-90 active:scale-[0.99]"
                    style={{ background: 'var(--theme-surface, hsl(var(--card)))', border: '1px solid var(--glass-border, hsl(var(--border)))' }}
                    data-testid={`search-result-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: 'var(--theme-accent, hsl(var(--primary)))' }}>
                        {result.reference}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {result.translation.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text, hsl(var(--foreground)))' }}>
                      "{result.text}"
                    </p>
                  </button>
                ))}
              </div>
            </ScrollArea>)
          ) : isParallel ? (
            /* Parallel reader — two independent panes, each with its
               own ScrollArea so they scroll separately. Pane 2 has
               its own Book/Chapter selectors inline above its column. */
            (<div className="flex-1 flex divide-x divide-border min-h-0">
              {/* ── Pane 1 ─────────────────────────────────────── */}
              <ScrollArea className="flex-1 bg-card" data-testid="bible-pane1-scroll">
                <div ref={chapterContentRef} {...bindChapterSwipe()} className="touch-pan-y">
                  <div className="px-6 py-5 lg:px-8">
                    <div className="text-[10px] font-semibold text-muted-foreground/60 mb-4 uppercase tracking-[0.15em] flex items-center justify-between">
                      <span>{translation1.toUpperCase()}</span>
                      <span className="opacity-50 normal-case tracking-normal">{localizeBook(selectedBook)} {selectedChapter}</span>
                    </div>
                    {renderVerses(verses1, translation1)}
                    {reference && reference.includes(':') && verses1.length > 0 && verses1.length <= 10 && (
                      <div className="flex justify-center pt-6 pb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const chapterRef = reference.replace(/:.*$/, '');
                            setReference(chapterRef);
                            manualLookupRef.current = true;
                            setSelectedVerse('');
                            fetchPassage(chapterRef);
                          }}
                          className="rounded-full text-xs gap-2 px-5"
                          style={{ color: 'var(--theme-accent, hsl(var(--primary)))', borderColor: 'var(--theme-accent, hsl(var(--primary)))' }}
                          data-testid="read-full-chapter-btn"
                        >
                          <BookOpen className="h-3.5 w-3.5" />
                          {t('generated.biblereader_read_full_chapter_4936')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
              {/* ── Pane 2 — independent navigation ──────────────── */}
              <div className="flex-1 flex flex-col min-w-0 min-h-0">
                {/* Compact selectors for pane 2 — keep the surface light
                    so the comparison reads as the same control surface
                    rather than a duplicate header. */}
                <div className="px-3 py-2 border-b border-border bg-muted/10 flex gap-2" data-testid="bible-pane2-controls">
                  <Select
                    value={selectedBook2}
                    onValueChange={(val) => { setSelectedBook2(val); setSelectedChapter2('1'); }}
                  >
                    <SelectTrigger className="bg-background border-input text-xs h-8 flex-1 min-w-0" data-testid="bible-pane2-book"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">{BIBLE_BOOKS.map(b => <SelectItem key={b.name} value={b.name}>{localizeBook(b.name)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={selectedChapter2} onValueChange={setSelectedChapter2}>
                    <SelectTrigger className="bg-background border-input text-xs h-8 w-16 shrink-0" data-testid="bible-pane2-chapter"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">{Array.from({ length: getChapterCount(selectedBook2) }, (_, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <ScrollArea className="flex-1 bg-card" data-testid="bible-pane2-scroll">
                  <div className="px-6 py-5 lg:px-8">
                    <div className="text-[10px] font-semibold text-muted-foreground/60 mb-4 uppercase tracking-[0.15em] flex items-center justify-between">
                      <span>{translation2.toUpperCase()}</span>
                      <span className="opacity-50 normal-case tracking-normal">{localizeBook(selectedBook2)} {selectedChapter2}</span>
                    </div>
                    {renderVerses(verses2, translation2, { book: selectedBook2, chapter: selectedChapter2 })}
                  </div>
                </ScrollArea>
              </div>
            </div>)
          ) : (
            /* Single-pane reader — unchanged classic layout. */
            (<ScrollArea className="flex-1 bg-card">
              <div ref={chapterContentRef} {...bindChapterSwipe()} className="touch-pan-y">
                <div className="px-6 py-5 lg:px-8">
                  <div className="text-[10px] font-semibold text-muted-foreground/60 mb-4 uppercase tracking-[0.15em] flex items-center justify-between">
                    <span>{translation1.toUpperCase()}</span>
                    <span className="opacity-50 normal-case tracking-normal">{localizeBook(selectedBook)} {selectedChapter}</span>
                  </div>
                  {renderVerses(verses1, translation1)}
                  {reference && reference.includes(':') && verses1.length > 0 && verses1.length <= 10 && (
                    <div className="flex justify-center pt-6 pb-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const chapterRef = reference.replace(/:.*$/, '');
                          setReference(chapterRef);
                          manualLookupRef.current = true;
                          setSelectedVerse('');
                          fetchPassage(chapterRef);
                        }}
                        className="rounded-full text-xs gap-2 px-5"
                        style={{ color: 'var(--theme-accent, hsl(var(--primary)))', borderColor: 'var(--theme-accent, hsl(var(--primary)))' }}
                        data-testid="read-full-chapter-btn"
                      >
                        <BookOpen className="h-3.5 w-3.5" />
                        {t('generated.biblereader_read_full_chapter_4936')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>)
          )}

          {/* Copyright Attribution Footer */}
          <CopyrightFooter translations={isParallel ? [translation1, translation2] : [translation1]} />

          {/* Floating "Discuss with Eili" + "Share" bar */}
          {selectMode && selectedVerses.size > 0 && (
            <div
              className="px-4 py-3 border-t border-primary/20 flex items-center justify-between gap-3 animate-in slide-in-from-bottom-2 duration-200"
              style={{ background: 'hsl(var(--primary) / 0.08)' }}
              data-testid="discuss-with-eili-bar"
            >
              <span className="text-sm font-medium text-primary">
                {selectedVerses.size} verse{selectedVerses.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleShareSelected}
                  className="gap-1.5"
                  data-testid="share-selected-verses-btn"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {t('generated.biblereader_share_8c02')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleDiscussSelected}
                  className="bg-primary text-primary-foreground hover:opacity-90 gap-1.5"
                  data-testid="discuss-with-eili-btn"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {t('generated.biblereader_discuss_a27a')}
                </Button>
              </div>
            </div>
          )}

          {/* Footer Navigation */}
          <div className="shrink-0 p-2.5 bg-muted/30 border-t border-border flex items-center justify-center gap-6 pb-4">
            <Button variant="ghost" size="icon" disabled={!canPrevChapter} onClick={handlePrevChapter} className={`h-8 w-8 hover:bg-background rounded-full ${!canPrevChapter ? 'invisible' : ''}`} title={t('bible.prev_chapter')}>
              <ChevronsLeft className="h-5 w-5 text-muted-foreground" />
            </Button>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest min-w-[120px] text-center">{localizeBook(selectedBook)} {selectedChapter}</p>
            <Button variant="ghost" size="icon" disabled={!canNextChapter} onClick={handleNextChapter} className={`h-8 w-8 hover:bg-background rounded-full ${!canNextChapter ? 'invisible' : ''}`} title={t('bible.next_chapter')}>
              <ChevronsRight className="h-5 w-5 text-muted-foreground" />
            </Button>
          </div>
        </>
      ) : activeTab === 'offline' ? (
        /* Offline Downloads Tab */
        (<OfflineDownloadPanel />)
      ) : (
        /* Highlights Tab — `min-h-0` is the critical fix. Without it
           the flex child grows with its content (default flex item
           min-height: auto), the ScrollArea Root's overflow:hidden
           clips the overflow, and there's nothing for the Viewport
           to scroll. Adding min-h-0 makes flex-1 actually constrain
           height to the available space, so the inner Viewport
           overflows and the scrollbar works. */
        (<ScrollArea className="flex-1 min-h-0 bg-card">
          <div className="p-4 space-y-3">
            {highlights.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Highlighter className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">{t('bible.no_highlights', 'No highlights yet')}</p>
                <p className="text-sm mt-1 opacity-70">{t('bible.highlight_hint', 'Tap any verse to highlight it')}</p>
              </div>
            ) : (
              highlights.map((hl, i) => (
                <SwipeableHighlightItem
                  key={`${hl.reference}-${hl.verse_num}-${i}`}
                  highlight={hl}
                  index={i}
                  onDelete={removeHighlight}
                  onNavigate={navigateToHighlight}
                />
              ))
            )}
          </div>
        </ScrollArea>)
      )}
      {/* ── Share Verse modal (long-press or multi-select toolbar) ── */}
      {shareTarget && (
        <ShareVerseModal
          reference={shareTarget.reference}
          verses={shareTarget.verses}
          translation={shareTarget.translation}
          onClose={() => setShareTarget(null)}
          onAddMoreVerses={
            // Only available when the modal was opened from a single
            // long-press (one-verse seed) — not when the user already
            // multi-selected a range and tapped Share.
            shareTarget._seedVerse
              ? () => {
                  // Drop the modal, enter select mode, preselect the
                  // seed verse so the user can extend the range.
                  const seed = new Set([shareTarget._seedVerse]);
                  setSelectedVerses(seed);
                  setSelectMode(true);
                  setShareTarget(null);
                }
              : undefined
          }
        />
      )}
      {signInModal}
    </div>
  );
}


const PUBLIC_TRANSLATIONS = [
  { id: 'kjv', name: 'King James Version' },
  { id: 'web', name: 'World English Bible' },
  { id: 'asv', name: 'American Standard Version' },
  { id: 'ylt', name: "Young's Literal Translation" },
  { id: 'rvr09', name: 'Reina-Valera 1909' },
  { id: 'ls1910', name: 'Louis Segond 1910' },
  { id: 'almeida', name: 'Almeida' },
];

function OfflineDownloadPanel() {
  const {
    t
  } = useTranslation();

  const [downloadCounts, setDownloadCounts] = useState({});
  const [downloading, setDownloading] = useState(null); // translation id
  const [progress, setProgress] = useState({ current: 0, total: 0, book: '' });

  useEffect(() => {
    getDownloadedTranslations().then(setDownloadCounts);
  }, []);

  const handleDownload = async (translationId) => {
    if (downloading) return;
    setDownloading(translationId);
    let totalDownloaded = 0;
    const totalChapters = BIBLE_BOOKS.reduce((sum, b) => sum + b.chapters.length, 0);

    for (const book of BIBLE_BOOKS) {
      setProgress({ current: totalDownloaded, total: totalChapters, book: book.name });
      try {
        const res = await axios.post(`${API}/bible/download-book`, {
          book: book.name,
          translation: translationId,
          total_chapters: book.chapters.length,
        });
        const entries = (res.data.chapters || [])
          .filter(ch => ch.data)
          .map(ch => ({
            reference: ch.data.reference,
            translation: translationId,
            data: ch.data,
          }));
        if (entries.length > 0) await bulkCachePassages(entries);
        totalDownloaded += book.chapters.length;
      } catch {
        totalDownloaded += book.chapters.length;
      }
    }

    setDownloading(null);
    setProgress({ current: 0, total: 0, book: '' });
    const updated = await getDownloadedTranslations();
    setDownloadCounts(updated);
    toast.success(`${translationId.toUpperCase()} downloaded for offline reading`);
  };

  const handleDelete = async (translationId) => {
    await deleteTranslationCache(translationId);
    const updated = await getDownloadedTranslations();
    setDownloadCounts(updated);
    toast.success(`${translationId.toUpperCase()} offline data removed`);
  };

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <ScrollArea className="flex-1 bg-card">
      <div className="p-4 space-y-3">
        <div className="text-center pb-2">
          <p className="text-sm font-medium text-foreground">{t('generated.biblereader_download_for_offline_1545')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('generated.biblereader_public_domain_translations_caf2')}</p>
        </div>

        {downloading && (
          <div className="rounded-xl border border-border p-3 bg-muted/20 space-y-2" data-testid="download-progress">
            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground font-medium">{t('generated.biblereader_downloading_b2e4')} {downloading.toUpperCase()}</span>
              <span className="text-muted-foreground">{pct}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[11px] text-muted-foreground">{progress.book}...</p>
          </div>
        )}

        {PUBLIC_TRANSLATIONS.map(tr => {
          const count = downloadCounts[tr.id] || 0;
          const isComplete = count >= TOTAL_BIBLE_CHAPTERS;
          const isDownloading = downloading === tr.id;
          const hasPartial = count > 0 && !isComplete;

          return (
            <div
              key={tr.id}
              className="flex items-center justify-between rounded-xl border border-border p-3 bg-card hover:bg-muted/20 transition-colors"
              data-testid={`offline-translation-${tr.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{tr.name}</p>
                <p className="text-xs text-muted-foreground">
                  {isComplete ? (
                    <span className="text-green-500 flex items-center gap-1"><CheckCircle className="h-3 w-3 inline" /> {t('generated.biblereader_available_offline_d3ca')}</span>
                  ) : hasPartial ? (
                    `${count} / ${TOTAL_BIBLE_CHAPTERS} chapters`
                  ) : (
                    'Not downloaded'
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1.5 ml-2">
                {isComplete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => handleDelete(tr.id)}
                    disabled={!!downloading}
                    data-testid={`delete-offline-${tr.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant={isComplete ? 'outline' : 'default'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => handleDownload(tr.id)}
                  disabled={!!downloading}
                  data-testid={`download-btn-${tr.id}`}
                >
                  {isDownloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : isComplete ? (
                    'Re-download'
                  ) : (
                    <>
                      <Download className="h-3.5 w-3.5 mr-1" />
                      {t('generated.biblereader_download_7729')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}

        <p className="text-[11px] text-muted-foreground text-center pt-2">
          {t('generated.biblereader_copyrighted_translations_esv_c134')}
        </p>
      </div>
    </ScrollArea>
  );
}
